/**
 * Navigation origin marker.
 *
 * Project pages (`/projects/**`) are reachable from two places: the Dashboard
 * (everyone) and the Admin page (admins only). The route alone can't tell them
 * apart, but the breadcrumb trail and the selected sidebar item must. So the
 * entry point stamps `?from=admin` onto the URL and every link inside the
 * subtree forwards it — a query param rather than router `state` so the context
 * survives a refresh or a shared link.
 *
 * Callers MUST also check that the user is an admin before acting on this. The
 * marker lives in a user-editable URL, so on its own it proves nothing.
 */

export const ORIGIN_PARAM = 'from';
export const ADMIN_ORIGIN = 'admin';

/** True when a location's query string carries the admin-origin marker. */
export function hasAdminOrigin(search: string): boolean {
  return new URLSearchParams(search).get(ORIGIN_PARAM) === ADMIN_ORIGIN;
}

/**
 * Forwards the admin-origin marker onto an internal path. Returns the path
 * untouched when `fromAdmin` is false, so Dashboard-originated links stay clean.
 */
export function withAdminOrigin(path: string, fromAdmin: boolean): string {
  if (!fromAdmin) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${ORIGIN_PARAM}=${ADMIN_ORIGIN}`;
}
